<template>
	<div class="p-0 min-h-screen bg-gray-100">
		<div class="bg-white px-6 py-4 border-b border-gray-200">
			<div class="mb-2">
				<router-link :to="`/theme/${themeId}/page/${pageId}`" class="text-gray-500 no-underline text-sm inline-flex items-center hover:text-orange-7">
					<i class="fa fa-arrow-left mr-2"></i>
					Back to Page Edit
				</router-link>
			</div>
			<div class="flex justify-between items-center">
				<h1 class="text-2xl font-semibold text-gray-900 m-0">Edit Page Object</h1>
				<button class="bg-orange-7 text-white border-none rounded-md px-4 py-2 text-sm font-medium cursor-pointer hover:bg-orange-6 disabled:opacity-50 disabled:cursor-not-allowed" @click="saveObject" :disabled="isSaving">
					{{ isSaving ? 'Saving...' : 'Save Changes' }}
				</button>
			</div>
		</div>

		<div class="p-6">
			<div v-if="isLoading" class="py-12 px-6 text-center text-gray-500">Loading page object...</div>

			<div v-else class="bg-white rounded-lg shadow-sm">
				<div class="flex justify-between items-center px-6 py-4 border-b border-gray-200">
					<h2 class="text-lg font-semibold text-gray-900 m-0">Object Properties</h2>
				</div>
				<div class="p-6">
					<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
						<div class="mb-0">
							<label class="block text-sm font-medium text-gray-700 mb-1">Name</label>
							<input v-model="pageObject.name" type="text" class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent">
						</div>
						<div class="mb-0">
							<label class="block text-sm font-medium text-gray-700 mb-1">Type</label>
							<input v-model="pageObject.type" type="text" class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent">
						</div>
						<div class="mb-0">
							<label class="block text-sm font-medium text-gray-700 mb-1">X</label>
							<input v-model.number="pageObject.x" type="number" class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent">
						</div>
						<div class="mb-0">
							<label class="block text-sm font-medium text-gray-700 mb-1">Y</label>
							<input v-model.number="pageObject.y" type="number" class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent">
						</div>
						<div class="mb-0">
							<label class="block text-sm font-medium text-gray-700 mb-1">Width</label>
							<input v-model.number="pageObject.width" type="number" class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent">
						</div>
						<div class="mb-0">
							<label class="block text-sm font-medium text-gray-700 mb-1">Height</label>
							<input v-model.number="pageObject.height" type="number" class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent">
						</div>
						<div class="mb-0">
							<label class="block text-sm font-medium text-gray-700 mb-1">Rotation</label>
							<input v-model.number="pageObject.rotation" type="number" class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent">
						</div>
						<div class="mb-0">
							<label class="block text-sm font-medium text-gray-700 mb-1">Z-Index</label>
							<input v-model.number="pageObject.zindex" type="number" class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent">
						</div>
						<div class="mb-0">
							<label class="block text-sm font-medium text-gray-700 mb-1">Opacity</label>
							<input v-model.number="pageObject.opacity" type="number" step="0.1" min="0" max="1" class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent">
						</div>
						<div class="mb-0">
							<label class="block text-sm font-medium text-gray-700 mb-1">Background Color</label>
							<input v-model="pageObject.backgroundColor" type="text" class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent" placeholder="#ffffff">
						</div>
						<div class="mb-0">
							<label class="block text-sm font-medium text-gray-700 mb-1">Border Width</label>
							<input v-model.number="pageObject.borderWidth" type="number" class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent">
						</div>
						<div class="mb-0">
							<label class="block text-sm font-medium text-gray-700 mb-1">Border Color</label>
							<input v-model="pageObject.borderColor" type="text" class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent" placeholder="#000000">
						</div>
						<div class="mb-0">
							<label class="block text-sm font-medium text-gray-700 mb-1">Border Radius</label>
							<input v-model.number="pageObject.borderRadius" type="number" class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent">
						</div>
					</div>
					<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
						<div class="p-3 border border-gray-200 rounded-md">
							<label class="flex items-center gap-2 cursor-pointer">
								<input
									type="checkbox"
									v-model="pageObject.locked"
									class="w-4 h-4"
								>
								<span>Locked</span>
							</label>
						</div>
						<div class="p-3 border border-gray-200 rounded-md">
							<label class="flex items-center gap-2 cursor-pointer">
								<input
									type="checkbox"
									v-model="pageObject.visible"
									class="w-4 h-4"
								>
								<span>Visible</span>
							</label>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { httpClient } from 'utils/http';
import Swal from 'sweetalert2';

export default defineComponent({
	name: 'PageObjectEditPage',
	setup() {
		const route = useRoute();
		const themeId = route.params.id as string;
		const pageId = route.params.pageid as string;
		const objectId = route.params.objectid as string;

		const pageObject = ref<any>({});
		const isLoading = ref(false);
		const isSaving = ref(false);

		const loadObject = async () => {
			isLoading.value = true;
			try {
				const { data } = await httpClient.get(`/api/pageobject/${objectId}`);
				pageObject.value = data;
			} catch (error: any) {
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to load page object',
					icon: 'error',
				});
			} finally {
				isLoading.value = false;
			}
		};

		const saveObject = async () => {
			isSaving.value = true;
			try {
				await httpClient.put(`/api/pageobject/${objectId}`, pageObject.value);
				Swal.fire({
					title: 'Saved',
					text: 'Page object has been saved',
					icon: 'success',
					timer: 1500,
				});
			} catch (error: any) {
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to save page object',
					icon: 'error',
				});
			} finally {
				isSaving.value = false;
			}
		};

		onMounted(() => {
			loadObject();
		});

		return {
			themeId,
			pageId,
			pageObject,
			isLoading,
			isSaving,
			saveObject,
		};
	},
});
</script>
